import * as _ from 'lodash';
import { useMemo } from 'react';
import { IOrganPickupDelivery } from '@nkr/models/index';
import { GridWrapper } from './styles';
import { AgGridReact, AgGridColumnProps, AgGridColumnGroupProps } from 'ag-grid-react';
import { IHeaderParams, GridReadyEvent, ValueGetterParams, FirstDataRenderedEvent, GridSizeChangedEvent, ICellRendererParams, SortChangedEvent } from 'ag-grid-community';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import ReactHtmlParser from 'react-html-parser';

type MyColumn = AgGridColumnProps | AgGridColumnGroupProps;

interface IProps {
    report: IOrganPickupDelivery[],
    onGridReady: (e: GridReadyEvent) => void
}

const OrganPickupDeliveryReportGrid = (props: IProps) => {
    const onGridReady = (e: GridReadyEvent) => {
        props.onGridReady(e);
        e.api.sizeColumnsToFit();
    };

    const getOverlay = (id: number, fieldName: string) => {
        let reportRecord = props.report.find(x => x.id == id);
        let fieldUpdate = '';

        switch (fieldName) {
            case 'pickupAddresses':
                fieldUpdate = reportRecord.pickupAddresses;
                break;
            case 'deliveryAddresses':
                fieldUpdate = reportRecord.deliveryAddresses;
                break;
            case 'notes':
                fieldUpdate = reportRecord.notes;
                break;
        }

        if (reportRecord != null)
            return (
                <Popover>
                    {ReactHtmlParser(fieldUpdate)}
                </Popover>
            );
        return <Popover></Popover>;
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        // get the current grids width
        let gridWidth = document.getElementById('grid-wrapper').offsetWidth;

        // keep track of which columns to hide/show
        let columnsToShow = [];
        let columnsToHide = [];

        // iterate over all columns (visible or not) and work out
        // now many columns can fit (based on their minWidth)
        let totalColsWidth = 0;
        let allColumns = e.columnApi.getAllColumns();

        for (let i = 0; i < allColumns.length; i++) {
            let column = allColumns[i];
            totalColsWidth += column.getMinWidth();

            if (totalColsWidth > gridWidth) {
                columnsToHide.push(column.getColId);
            } else {
                columnsToShow.push(column.getColId);
            }
        }

        // show/hide columns based on current grid width
        e.columnApi.setColumnsVisible(columnsToShow, true);
        e.columnApi.setColumnsVisible(columnsToHide, false);

        // fill out any available space to ensure there are no gaps
        e.api.sizeColumnsToFit();
    };

    const reportColumns: MyColumn[] = [
        {
            colId: 'RowNumber',
            field: 'R#',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    R#
                </SortableHeaderComponent>,
            headerName: 'Rank',
            width: 55,
            sortable: false,
            suppressSizeToFit: true,
            valueGetter: (props: ValueGetterParams) => props.node.rowIndex + 1
        },
        {
            colId: 'centerAlias',
            field: 'centerAlias',
            headerName: 'Center Alias',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Center Alias
                </SortableHeaderComponent>,

            width: 150,
        },
        {
            colId: 'centerName',
            field: 'centerName',
            headerName: 'Center Name',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Center Name
                </SortableHeaderComponent>,
            width: 350,
        },
        {
            colId: 'earlyPU',
            field: 'earlyPU',
            headerName: 'Early P/U',
            width: 100,
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Early P/U
                </SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IOrganPickupDelivery;
                let timezone = data.centerTimeZone;
                let earlyPU = data.earlyPU;

                return earlyPU + ' ' + timezone;
            },
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as IOrganPickupDelivery;
                return data.earlyPickupUtc;
            }
        },
        {
            colId: 'latestDT',
            field: 'latestDT',
            headerName: 'Latest Delivery Time',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Latest<br />Delivery Time
                </SortableHeaderComponent>,
            width: 140,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IOrganPickupDelivery;
                let latestDT = data.latestDT;
                let timezone = data.centerTimeZone;

                return latestDT + ' ' + timezone;
            },
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as IOrganPickupDelivery;
                return data.latestDeliveryUtc;
            }
        },
        {
            colId: 'pickupAddresses',
            field: 'pickupAddresses',
            headerName: 'Pickup Addresses',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Pickup Addresses
                </SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) =>
                <div>
                    <OverlayTrigger trigger={['hover', 'focus']} placement='bottom' overlay={getOverlay(props.data.id, 'pickupAddresses')}>
                        <div>
                            {props.data.pickupAddresses == null || props.data.pickupAddresses.length == 0
                                ? ''
                                : props.data.pickupAddresses.substring(0, 30) + '...'}
                        </div>
                    </OverlayTrigger>
                </div>,
            width: 300,
            sortable: false
        },
        {
            colId: 'pickupCity',
            field: 'pickupCity',
            headerName: 'pickupCity',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    City
                </SortableHeaderComponent>,
            width: 150,
        },
        {
            colId: 'pickupState',
            field: 'pickupState',
            headerName: 'pickupState',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    State
                </SortableHeaderComponent>,
            width: 100,
        },
        {
            colId: 'pickupPostalCode',
            field: 'pickupPostalCode',
            headerName: 'pickupPostalCode',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Postal Code
                </SortableHeaderComponent>,
            width: 120,
        },
        {
            colId: 'deliveryAddresses',
            field: 'deliveryAddresses',
            headerName: 'deliveryAddresses',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Delivery Addresses
                </SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) =>
                <div>
                    <OverlayTrigger trigger={['hover', 'focus']} placement='bottom' overlay={getOverlay(props.data.id, 'deliveryAddresses')}>
                        <div>
                            {props.data.deliveryAddresses == null || props.data.deliveryAddresses.length == 0
                                ? ''
                                : props.data.deliveryAddresses.substring(0, 30) + '...'}
                        </div>
                    </OverlayTrigger>
                </div>,
            width: 300,
            sortable: false
        },
        {
            colId: 'deliveryCity',
            field: 'deliveryCity',
            headerName: 'deliveryCity',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    City
                </SortableHeaderComponent>,
            width: 150,
        },
        {
            colId: 'deliveryState',
            field: 'deliveryState',
            headerName: 'deliveryState',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    State
                </SortableHeaderComponent>,
            width: 100,
        },
        {
            colId: 'deliveryPostalCode',
            field: 'deliveryPostalCode',
            headerName: 'deliveryPostalCode',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Postal Code
                </SortableHeaderComponent>,
            width: 120,
        },
        {
            colId: 'notes',
            field: 'notes',
            headerName: 'Notes',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Notes
                </SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) =>
                <div>
                    <OverlayTrigger trigger={['hover', 'focus']} placement='bottom' overlay={getOverlay(props.data.id, 'notes')}>
                        <div>
                            {props.data.notes == null || props.data.notes.length == 0
                                ? ''
                                : props.data.notes.substring(0, 25) + '...'}
                        </div>
                    </OverlayTrigger>
                </div>,
            width: 250,
            sortable: false
        }
    ];

    const columnDefs = useMemo(() => reportColumns, [props.report]);

    return (
        <>
            <GridWrapper id='grid-wrapper' className='container-fluid-max-sm ag-theme-bootstrap' style={{ marginBottom: 20 }}>
                <AgGridReact
                    defaultColDef={{
                        cellClass: 'center-list-cell',
                        headerClass: 'center-list-header',
                        sortable: true,
                        resizable: true
                    }}
                    columnDefs={columnDefs}
                    rowData={props.report}
                    enableCellTextSelection={true}
                    enableRangeSelection={true}
                    headerHeight={42}
                    noRowsOverlayComponentFramework={() =>
                        <div>No data for the selected date range...</div>}
                    onGridReady={onGridReady}
                    onGridSizeChanged={onGridSizeChanged}
                    onFirstDataRendered={(e: FirstDataRenderedEvent) => e.api.sizeColumnsToFit()}
                    suppressCellSelection={true}
                    onSortChanged={(e: SortChangedEvent) => e.api.refreshCells()}
                />
            </GridWrapper>

        </>
    );
}

export default OrganPickupDeliveryReportGrid;